import React, { useState, useEffect } from 'react';
import {
  FaPlus,
  FaTimes,
  FaPaperPlane,
  FaImage,
  FaCheckCircle,
  FaClock,
  FaExclamationCircle,
  FaHeadset,
  FaArrowLeft
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getData, postData } from '../../../../Services/Fetchnodeservices';

const SupportScreen = ({ restaurantId = 1, isDark = false }) => {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  
  // Form State
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // 🎨 ZAPTODOOR ORANGE THEME CONFIGURATION
  const theme = isDark ? {
    background: '#0f172a',
    surface: '#1e293b',
    card: '#1e293b',
    primary: '#f97316',
    primaryLight: '#fb923c',
    textPrimary: '#f8fafc',
    textSecondary: '#94a3b8',
    textMuted: '#64748b',
    border: 'rgba(249, 115, 22, 0.2)',
    inputBackground: '#0f172a',
    inputText: '#f8fafc',
    inputPlaceholder: '#94a3b8',
    inputBorder: 'rgba(249, 115, 22, 0.2)',
    error: '#dc2626',
    success: '#16a34a',
    warning: '#f59e0b',
    shadow: '0 4px 12px rgba(0,0,0,0.3)',
    shadowActive: '0 8px 24px rgba(249, 115, 22, 0.15)',
  } : {
    background: '#fff7ed',
    surface: '#ffffff',
    card: '#ffffff',
    primary: '#f97316',
    primaryLight: '#fb923c',
    textPrimary: '#0f172a',
    textSecondary: '#64748b',
    textMuted: '#94a3b8',
    border: '#fed7aa',
    inputBackground: '#ffffff',
    inputText: '#0f172a',
    inputPlaceholder: '#94a3b8',
    inputBorder: '#fed7aa',
    error: '#dc2626',
    success: '#16a34a',
    warning: '#f59e0b',
    shadow: '0 1px 3px rgba(0,0,0,0.06)',
    shadowActive: '0 8px 24px rgba(249, 115, 22, 0.12)',
  };

  // 1. Fetch Tickets
  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await getData(`supportRoutes?user_table=restaurants&user_id=${restaurantId}`);
      if (res.success) {
        setTickets(res.data);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [restaurantId]);

  // 2. Handle Image Upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSelectedImage({
          uri: event.target.result,
          file: file,
          name: file.name,
          type: file.type,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  // 3. Remove Image
  const removeImage = () => {
    setSelectedImage(null);
  };

  // 4. Submit Ticket (With FormData for Image)
  const handleSubmitTicket = async () => {
    if (!subject.trim() || !description.trim()) {
      alert('Please fill in the subject and description');
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('user_table', 'restaurants');
      formData.append('user_id', restaurantId);
      formData.append('subject', subject);
      formData.append('description', description);

      if (selectedImage && selectedImage.file) {
        formData.append('screenshot', selectedImage.file);
      }

      const result = await postData('supportRoutes/create', formData);

      if (result.success) {
        alert('Your ticket has been raised successfully!');
        setModalVisible(false);
        setSubject('');
        setDescription('');
        setSelectedImage(null);
        fetchTickets();
      } else {
        alert(result.message || 'Something went wrong');
      }
    } catch (error) {
      console.error(error);
      alert('Connection failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Get status style
  const getStatusStyle = (status) => {
    switch (status) {
      case 'open': return { bg: isDark ? 'rgba(249, 115, 22, 0.15)' : '#fff7ed', text: theme.primary, icon: <FaExclamationCircle /> };
      case 'in_progress': return { bg: isDark ? 'rgba(59, 130, 246, 0.15)' : '#eff6ff', text: '#3b82f6', icon: <FaClock /> };
      case 'resolved': return { bg: isDark ? 'rgba(22, 163, 74, 0.15)' : '#f0fdf4', text: theme.success, icon: <FaCheckCircle /> };
      default: return { bg: isDark ? 'rgba(100, 116, 139, 0.15)' : '#f1f5f9', text: theme.textMuted, icon: null };
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Ticket Card Component
  const TicketCard = ({ ticket }) => {
    const statusStyle = getStatusStyle(ticket.ticketstatus);
    return (
      <div className="ticket-card" style={{
        ...styles.ticketCard,
        backgroundColor: theme.card,
        borderColor: theme.border,
        boxShadow: theme.shadow
      }}>
        <div style={styles.cardHeader}>
          <div style={{ ...styles.statusBadge, backgroundColor: statusStyle.bg }}>
            <span style={{ ...styles.statusText, color: statusStyle.text }}>
              {statusStyle.icon} {ticket.ticketstatus.toUpperCase().replace('_', ' ')}
            </span>
          </div>
          <span style={{...styles.dateText, color: theme.textMuted}}>{formatDate(ticket.created_at)}</span>
        </div>
        <h4 style={{...styles.subjectText, color: theme.textPrimary}}>{ticket.subject}</h4>
        <p style={{...styles.descText, color: theme.textSecondary}}>{ticket.description}</p>
        <div style={styles.cardFooter}>
          <span style={{...styles.idText, color: theme.textMuted}}>ID: #{ticket.id}</span>
          {ticket.screenshot && (
            <span style={{...styles.attachmentBadge, color: theme.success}}>
              <FaImage size={12} /> Attachment
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div style={{ ...styles.container, backgroundColor: theme.background }}>
      {/* Header */}
      <div style={{...styles.header, backgroundColor: theme.surface, borderBottomColor: theme.border}}>
        <div>
          <h1 style={{ ...styles.headerTitle, color: theme.textPrimary }}>
            Support <span style={{ color: theme.primary }}>Hub</span>
          </h1>
          <p style={{ color: theme.textSecondary }}>We're here to help you</p>
        </div>
        <div style={styles.headerActions}>
          <button className="back-btn" style={{...styles.backBtn, color: theme.textPrimary, borderColor: theme.border}} onClick={() => navigate(-1)}>
            <FaArrowLeft size={16} />
            <span>Back</span>
          </button>
          <button
            className="add-btn"
            style={{ ...styles.addButton, backgroundColor: theme.primary }}
            onClick={() => setModalVisible(true)}
          >
            <FaPlus size={20} color="#FFFFFF" />
          </button>
        </div>
      </div>

      {/* Tickets List */}
      {loading ? (
        <div style={styles.loaderContainer}>
          <div className="spinner" style={{borderColor: theme.border, borderTopColor: theme.primary}}></div>
          <p style={{ color: theme.textSecondary, marginTop: 12 }}>Loading tickets...</p>
        </div>
      ) : tickets.length === 0 ? (
        <div style={styles.emptyContainer}>
          <FaHeadset size={48} color={theme.textMuted} />
          <p style={{ color: theme.textMuted, marginTop: 12, fontSize: '16px', fontWeight: 600 }}>No tickets found.</p>
          <p style={{ color: theme.textSecondary, fontSize: 14 }}>
            Click the + button to raise a new support ticket
          </p>
        </div>
      ) : (
        <div style={styles.listContainer}>
          {tickets.map((ticket) => (
            <TicketCard key={ticket.id} ticket={ticket} />
          ))}
        </div>
      )}

      {/* Modal */}
      {modalVisible && (
        <div style={styles.modalOverlay} onClick={() => setModalVisible(false)}>
          <div style={{ ...styles.modalContent, backgroundColor: theme.card }} onClick={(e) => e.stopPropagation()}>
            <div style={{...styles.modalHeader, borderBottomColor: theme.border, backgroundColor: theme.card}}>
              <h3 style={{ ...styles.modalTitle, color: theme.textPrimary }}>New Support Request</h3>
              <button className="close-btn" style={styles.closeBtn} onClick={() => setModalVisible(false)}>
                <FaTimes size={20} color={theme.error} />
              </button>
            </div>

            <div style={styles.modalBody}>
              {/* Subject */}
              <div style={styles.formGroup}>
                <label style={{...styles.label, color: theme.textSecondary}}>Subject *</label>
                <input
                  style={{ 
                    ...styles.input, 
                    backgroundColor: theme.inputBackground, 
                    borderColor: theme.inputBorder,
                    color: theme.inputText
                  }}
                  placeholder="Brief title of your issue"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
              </div>

              {/* Description */}
              <div style={styles.formGroup}>
                <label style={{...styles.label, color: theme.textSecondary}}>Message *</label>
                <textarea
                  style={{ 
                    ...styles.textarea, 
                    backgroundColor: theme.inputBackground, 
                    borderColor: theme.inputBorder,
                    color: theme.inputText
                  }}
                  placeholder="How can we help you?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={5}
                />
              </div>

              {/* Image Upload */}
              <div style={styles.formGroup}>
                <label style={{...styles.label, color: theme.textSecondary}}>Attachment (Optional)</label>
                <div style={{ 
                  ...styles.imageBox, 
                  borderColor: selectedImage ? theme.success : theme.primary, 
                  backgroundColor: theme.inputBackground 
                }}>
                  {selectedImage ? (
                    <div className="image-preview-container" style={styles.imagePreviewContainer}>
                      <img src={selectedImage.uri} alt="Attachment" style={styles.imagePreview} />
                      <button className="remove-image-btn" style={styles.removeImageBtn} onClick={removeImage}>
                        <FaTimes size={16} color="#FFFFFF" />
                      </button>
                    </div>
                  ) : (
                    <label style={styles.uploadLabel}>
                      <FaImage size={24} color={theme.primary} />
                      <span style={{color: theme.textSecondary, fontSize: '14px', fontWeight: 500}}>Click to upload screenshot</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        style={styles.fileInput}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <button
                className="submit-btn"
                style={{ ...styles.submitBtn, backgroundColor: theme.primary, opacity: submitting ? 0.7 : 1 }}
                onClick={handleSubmitTicket}
                disabled={submitting}
              >
                {submitting ? (
                  <div className="spinner-small"></div>
                ) : (
                  <>
                    <FaPaperPlane size={18} color="#FFFFFF" />
                    <span style={styles.submitText}>Submit Ticket</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideUp {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .spinner {
          border: 3px solid ${theme.border};
          border-top: 3px solid ${theme.primary};
          border-radius: 50%;
          width: 40px;
          height: 40px;
          animation: spin 0.8s linear infinite;
        }
        .spinner-small {
          border: 2px solid #FFFFFF;
          border-top: 2px solid transparent;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          animation: spin 0.8s linear infinite;
        }
        .back-btn:hover {
          background: ${isDark ? 'rgba(249, 115, 22, 0.15)' : '#fff7ed'};
          transform: translateX(-2px);
        }
        .ticket-card:hover {
          transform: translateY(-2px);
          box-shadow: ${theme.shadowActive} !important;
        }
        .add-btn:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(249, 102, 241, 0.3);
        }
        .submit-btn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(249, 115, 22, 0.3);
        }
        .close-btn:hover {
          transform: rotate(90deg);
        }
        .image-preview-container:hover .remove-image-btn {
          opacity: 1 !important;
        }
        input:focus, textarea:focus {
          outline: none;
          border-color: ${theme.primary} !important;
          box-shadow: 0 0 0 3px ${isDark ? 'rgba(249, 115, 22, 0.15)' : 'rgba(249, 115, 22, 0.1)'} !important;
        }
      `}</style>
    </div>
  );
};

// Styles
const styles = {
  container: {
    minHeight: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    transition: 'background-color 0.3s ease',
  },
  header: {
    padding: '24px 30px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    position: 'sticky',
    top: 0,
    zIndex: 10,
    transition: 'all 0.3s ease',
  },
  headerTitle: {
    fontSize: '28px',
    fontWeight: 800,
    margin: 0,
    letterSpacing: '-0.5px',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '10px',
    borderWidth: '1px',
    borderStyle: 'solid',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 600,
    transition: 'all 0.2s ease',
  },
  addButton: {
    width: '48px',
    height: '48px',
    borderRadius: '14px',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 8px rgba(249, 115, 22, 0.2)',
  },
  listContainer: {
    padding: '20px 30px',
    maxWidth: '900px',
    margin: '0 auto',
    width: '100%',
  },
  ticketCard: {
    borderRadius: '16px',
    padding: '18px 20px',
    marginBottom: '14px',
    borderWidth: '1px',
    borderStyle: 'solid',
    transition: 'all 0.2s ease',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: '8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  statusText: {
    fontSize: '10px',
    fontWeight: 800,
    letterSpacing: '0.5px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  dateText: {
    fontSize: '11px',
  },
  subjectText: {
    fontSize: '17px',
    fontWeight: 700,
    margin: '0 0 6px 0',
  },
  descText: {
    fontSize: '13px',
    lineHeight: '1.5',
    margin: '0 0 10px 0',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '8px',
  },
  idText: {
    fontSize: '11px',
  },
  attachmentBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    fontWeight: 600,
  },
  loaderContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '300px',
    padding: '40px 20px',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.6)',
    backdropFilter: 'blur(4px)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    padding: '20px',
  },
  modalContent: {
    borderRadius: '24px',
    maxWidth: '600px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    animation: 'slideUp 0.3s ease',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    position: 'sticky',
    top: 0,
    zIndex: 1,
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
  },
  modalTitle: {
    fontSize: '20px',
    fontWeight: 700,
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    transition: 'transform 0.2s ease',
  },
  modalBody: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 600,
    display: 'block',
    marginBottom: '6px',
  },
  input: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    borderWidth: '1px',
    borderStyle: 'solid',
    fontSize: '15px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    transition: 'all 0.2s ease',
  },
  textarea: {
    width: '100%',
    padding: '12px 16px',
    borderRadius: '12px',
    borderWidth: '1px',
    borderStyle: 'solid',
    fontSize: '15px',
    fontFamily: 'inherit',
    boxSizing: 'border-box',
    resize: 'vertical',
    minHeight: '120px',
    transition: 'all 0.2s ease',
  },
  imageBox: {
    height: '160px',
    borderRadius: '12px',
    borderWidth: '2px',
    borderStyle: 'dashed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    position: 'relative',
    transition: 'all 0.2s ease',
  },
  imagePreviewContainer: {
    width: '100%',
    height: '100%',
    position: 'relative',
  },
  imagePreview: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
  },
  removeImageBtn: {
    position: 'absolute',
    top: '8px',
    right: '8px',
    backgroundColor: 'rgba(239, 68, 68, 0.9)',
    border: 'none',
    borderRadius: '50%',
    width: '28px',
    height: '28px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    opacity: 0,
    transition: 'opacity 0.2s ease',
  },
  uploadLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
  },
  fileInput: {
    display: 'none',
  },
  submitBtn: {
    width: '100%',
    padding: '14px',
    borderRadius: '14px',
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontWeight: 600,
    fontSize: '16px',
    color: '#FFFFFF',
    marginTop: '10px',
  },
  submitText: {
    color: '#FFFFFF',
    fontWeight: 700,
    fontSize: '16px',
  },
};

export default SupportScreen;